import { useEffect, useRef } from 'react'
import { CameraControls } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import type { PlaneView } from './GeometryPreview'

/**
 * CAD-grade camera for the Agent-flow part preview.
 *
 * Wraps drei's <CameraControls> for damped orbit, dolly-to-cursor zoom, and *animated*
 * transitions to the Top/Front/Iso/Right views.
 *
 * The camera frames the part by measuring the REAL bounding box of what's actually in the
 * scene (skipping helpers via `userData.excludeFromFit`), then placing the camera so that
 * box is centered and fully fit for the current viewport aspect ratio. Re-running on view,
 * content, AND canvas-size changes is what keeps the part centered in the small panel, in
 * fullscreen, and on the way back.
 *
 * Only used by the agent-flow GeometryPreview wrapper — the toolpath viewer keeps its own
 * OrbitControls via GeometryPreviewScene's default `controls="orbit"`.
 */

// Unit direction the camera sits along for each named view (scene space: y up, z = -cnc_y).
const VIEW_DIRS: Record<PlaneView, [number, number, number]> = {
  // tiny z nudge on top avoids gimbal lock looking straight down
  top: [0, 1, 0.0001],
  front: [0, 0, 1],
  right: [1, 0, 0],
  iso: [0.6, 0.8, 0.6],
}

// Scratch objects reused across runs to avoid per-fit allocation.
const _box = new THREE.Box3()
const _sphere = new THREE.Sphere()
const _center = new THREE.Vector3()
const _dir = new THREE.Vector3()

export function CameraRig({
  view,
  viewKey,
  geometry,
  stepMeshes,
  showStock,
}: {
  view: PlaneView
  viewKey: number
  // Content-affecting inputs — used only as effect triggers so the fit re-runs when the
  // rendered geometry changes (new part, STEP tessellation completes, stock toggled).
  geometry: Record<string, unknown>
  stepMeshes: unknown
  showStock: boolean
}) {
  const ref = useRef<CameraControls>(null)
  const scene = useThree((s) => s.scene)
  const camera = useThree((s) => s.camera) as THREE.PerspectiveCamera
  const width = useThree((s) => s.size.width)
  const height = useThree((s) => s.size.height)

  useEffect(() => {
    const controls = ref.current
    if (!controls) return

    // Measure the real part bounds. Prefer geometry under a `fitTarget` group (the actual
    // part — excludes dimension leaders, which can live in a different coordinate frame and
    // skew the center). Fall back to all non-helper meshes if nothing is tagged yet.
    _box.makeEmpty()
    let sawFitTarget = false
    const underFitTarget = (obj: THREE.Object3D): boolean => {
      for (let o: THREE.Object3D | null = obj; o; o = o.parent) {
        if (o.userData?.fitTarget) return true
      }
      return false
    }
    scene.traverse((obj) => {
      const o = obj as THREE.Mesh & THREE.Line
      if (!o.isMesh && !o.isLine) return
      if (underFitTarget(obj)) sawFitTarget = true
    })
    scene.traverse((obj) => {
      const o = obj as THREE.Mesh & THREE.Line
      if (!o.isMesh && !o.isLine) return
      if (sawFitTarget ? !underFitTarget(obj) : obj.userData?.excludeFromFit) return
      _box.expandByObject(obj)
    })
    if (_box.isEmpty()) return

    _box.getCenter(_center)
    const radius = _box.getBoundingSphere(_sphere).radius
    if (!(radius > 0)) return

    // Distance that fits the bounding sphere along the *limiting* (smaller) FOV axis, so
    // the part fits whether the viewport is tall or wide. + margin for breathing room.
    const vFov = (camera.fov * Math.PI) / 180
    const aspect = width / Math.max(height, 1)
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect)
    const dist = (radius / Math.sin(Math.min(vFov, hFov) / 2)) * 1.15

    _dir.set(...(VIEW_DIRS[view] ?? VIEW_DIRS.iso)).normalize()
    const px = _center.x + _dir.x * dist
    const py = _center.y + _dir.y * dist
    const pz = _center.z + _dir.z * dist

    // `true` → animate the transition. drei's CameraControls pumps frames for the
    // animation; under frameloop="always" every frame renders regardless.
    void controls.setLookAt(px, py, pz, _center.x, _center.y, _center.z, true)
  }, [view, viewKey, scene, camera, width, height, geometry, stepMeshes, showStock])

  return (
    <CameraControls
      ref={ref}
      makeDefault
      dollyToCursor
      smoothTime={0.25}
      draggingSmoothTime={0.12}
    />
  )
}
